import type { PaperProps } from '@mui/material/Paper';
import type { PopperProps } from '@mui/material/Popper';
import { Typography, Box, useTheme } from '@mui/material';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import type { TimePickerProps } from '@mui/x-date-pickers/TimePicker';
import { type ComponentType } from 'react';
import type { TouchEvent } from 'react';

type PopperSlotProps = NonNullable<NonNullable<TimePickerProps['slotProps']>['popper']>;
type DesktopPaperSlotProps = NonNullable<NonNullable<TimePickerProps['slotProps']>['desktopPaper']>;
type PopperSlotFn = Extract<PopperSlotProps, (...args: never[]) => unknown>;
type PopperOwnerState = Parameters<PopperSlotFn>[0];
type DesktopPaperSlotFn = Extract<DesktopPaperSlotProps, (...args: never[]) => unknown>;
type DesktopPaperOwnerState = Parameters<DesktopPaperSlotFn>[0];

const touchScrollClockSx = {
  '& .MuiPaper-root': { overscrollBehavior: 'contain' as const },
  '& .MuiMultiSectionDigitalClockSection-root, & .MuiDigitalClock-root, & .MuiDigitalClock-list': {
    touchAction: 'pan-y' as const,
    overscrollBehavior: 'contain' as const,
    WebkitOverflowScrolling: 'touch' as const,
  },
};

function stopTouchMoveFromEscaping(e: TouchEvent) {
  e.stopPropagation();
}

/**
 * Mobile: vertical drags on the digital clock were scrolling the parent slot list instead of the
 * hour/minute columns. Stop touchmove bubbling and constrain overscroll on the clock sections.
 * Bubble-phase touchmove on Popper and desktop Paper only (never capture) — stopPropagation keeps
 * drags from reaching the slot list after the clock has handled the gesture.
 */
function mergePickerPopperSlotPropsObject(userPopper: Partial<PopperProps> | undefined): Partial<PopperProps> {
  const obj = userPopper ?? {};
  const userOnTouchMove = obj.onTouchMove;
  const sxMerged = [touchScrollClockSx, ...(obj.sx == null ? [] : Array.isArray(obj.sx) ? obj.sx : [obj.sx])];
  return {
    ...obj,
    sx: sxMerged,
    // Bubble phase only — do NOT use onTouchMoveCapture + stopPropagation: capture runs before the
    // event reaches the digital clock columns, so iOS never scrolls the hour/minute lists.
    onTouchMove: (e) => {
      userOnTouchMove?.(e);
      stopTouchMoveFromEscaping(e);
    },
  };
}

function mergePickerPopperSlotProps(userPopper: PopperSlotProps | undefined): PopperSlotProps {
  if (userPopper === undefined) {
    return mergePickerPopperSlotPropsObject(undefined);
  }
  if (typeof userPopper === 'function') {
    return (ownerState: PopperOwnerState) => mergePickerPopperSlotPropsObject(userPopper(ownerState));
  }
  return mergePickerPopperSlotPropsObject(userPopper);
}

function mergeDesktopPaperSlotPropsObject(userPaper: Partial<PaperProps> | undefined): Partial<PaperProps> {
  const obj = userPaper ?? {};
  const userOnTouchMove = obj.onTouchMove;
  const sxMerged = [touchScrollClockSx, ...(obj.sx == null ? [] : Array.isArray(obj.sx) ? obj.sx : [obj.sx])];
  return {
    ...obj,
    sx: sxMerged,
    onTouchMove: (e) => {
      userOnTouchMove?.(e);
      stopTouchMoveFromEscaping(e);
    },
  };
}

function mergeDesktopPaperSlotProps(userPaper: DesktopPaperSlotProps | undefined): DesktopPaperSlotProps {
  if (userPaper === undefined) {
    return mergeDesktopPaperSlotPropsObject(undefined);
  }
  if (typeof userPaper === 'function') {
    return (ownerState: DesktopPaperOwnerState) => mergeDesktopPaperSlotPropsObject(userPaper(ownerState));
  }
  return mergeDesktopPaperSlotPropsObject(userPaper);
}

export default function TimePicker(
  props: TimePickerProps & {
    label?: string;
    icon?: ComponentType;
    durationMode?: boolean;
    disabled?: boolean;
    forceDesktopMode?: boolean;
  },
) {
  const { label, icon, durationMode = false, forceDesktopMode = false, slotProps: userSlotProps, ...rest } = props;
  const theme = useTheme();

  const mergedSlotProps = {
    ...userSlotProps,
    textField: {
      placeholder: 'HH:MM',
      ...userSlotProps?.textField,
    },
    popper: mergePickerPopperSlotProps(userSlotProps?.popper),
    desktopPaper: mergeDesktopPaperSlotProps(userSlotProps?.desktopPaper),
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }} role="group" aria-label={label}>
      {props.label && (
        <Typography
          variant="caption"
          sx={{
            color: props.disabled ? 'text.disabled' : 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: 1.25,
            letterSpacing: '0.00938em',
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={
          props.disabled
            ? {
                '& .MuiPickersInputBase-root.Mui-disabled': {
                  '& .MuiPickersSectionList-sectionContent': {
                    color: `${theme.palette.text.disabled} !important`,
                  },
                  '& .MuiPickersInputBase-sectionAfter': {
                    color: `${theme.palette.text.disabled} !important`,
                  },
                  '& .MuiPickersInputBase-sectionBefore': {
                    color: `${theme.palette.text.disabled} !important`,
                  },
                },
              }
            : {}
        }
      >
        <MuiTimePicker
          {...rest}
          ampm={false}
          views={['hours', 'minutes']}
          disabled={props.disabled}
          desktopModeMediaQuery={durationMode || forceDesktopMode ? '@media (min-width: 0px)' : undefined}
          slots={
            icon
              ? {
                  openPickerIcon: icon,
                }
              : undefined
          }
          slotProps={mergedSlotProps}
        />
      </Box>
    </Box>
  );
}
